import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
  useMemo,
} from 'react';
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import { Theme } from './themesTypes';
import { themes } from './themeStyle';
import { createMuiTheme } from './muiTheme';
import { LOG } from '../../../common/Utilities';

import '@fontsource/manrope';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextType | undefined>(
  undefined
);

const THEME_STORAGE_KEY = 'theme';

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [currentTheme, setCurrentTheme] = useState<Theme>(() => {
    // Sync with v2 dark mode preference
    const v2Theme = localStorage.getItem('v2-theme');
    if (v2Theme === 'dark') {
      return themes.dark;
    }
    if (v2Theme === 'light') {
      return themes.light;
    }
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    return savedTheme ? JSON.parse(savedTheme) : themes.light;
  });

  // Sync with v2 dark mode and load saved theme
  useEffect(() => {
    const v2Theme = localStorage.getItem('v2-theme');
    if (v2Theme === 'dark') {
      setCurrentTheme(themes.dark);
      return;
    }
    if (v2Theme === 'light') {
      setCurrentTheme(themes.light);
      return;
    }
    if ((global as any).me?.email !== 'admin-paiv@gogamify.com') {
      setCurrentTheme(themes.light);
      return;
    }
    const loadTheme = () => {
      try {
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme) {
          const parsedTheme = JSON.parse(savedTheme);
          setCurrentTheme(parsedTheme);
          LOG.debug('Theme set to:', parsedTheme);
        }
      } catch (error) {
        console.error('Failed to load theme:', error);
      }
    };
    loadTheme();
  }, []);

  // Listen for v2 theme changes via storage events and DOM mutations
  useEffect(() => {
    const syncV2Theme = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setCurrentTheme(isDark ? themes.dark : themes.light);
    };

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class'
        ) {
          syncV2Theme();
        }
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  // Toggle theme and persist selection
  const toggleTheme = () => {
    setCurrentTheme((prevTheme) => {
      const newTheme = prevTheme.isDark ? themes.light : themes.dark;
      localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(newTheme));
      localStorage.setItem('v2-theme', newTheme.isDark ? 'dark' : 'light');
      if (newTheme.isDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      return newTheme;
    });
  };

  // Create the MUI theme using our custom theme
  const muiTheme = useMemo(() => createMuiTheme(currentTheme), [currentTheme]);

  return (
    <ThemeContext.Provider value={{ theme: currentTheme, toggleTheme }}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
